import type { CSSProperties } from "react";
import type { Announcement } from "@excalidraw-ms/shared";

interface AnnouncementBannerProps {
  announcement: Announcement;
  onDismiss: (id: string) => void;
}

/**
 * An editorial notice about a change to the product.
 *
 * Uses role="status" rather than "alert": an announcement is good news that can
 * wait for a natural pause in screen-reader output, and interrupting for it
 * would be indistinguishable from an outage.
 *
 * Geometry mirrors BackendAccessDeniedNotice so the footer stack reads as one
 * surface; only the palette differs, to separate "something changed for the
 * better" from "something is constraining you".
 *
 * See docs/designs/message-taxonomy.md § 5.
 */
export function AnnouncementBanner({ announcement, onDismiss }: AnnouncementBannerProps) {
  return (
    <div role="status" style={bannerStyle} data-testid="announcement-banner">
      <span aria-hidden="true" style={{ fontSize: 16, marginRight: 10 }}>
        🎉
      </span>
      <span style={{ flex: 1, fontSize: 13, lineHeight: 1.4 }}>
        <strong>{announcement.title}</strong> {announcement.body}
      </span>
      <button
        type="button"
        onClick={() => onDismiss(announcement.id)}
        style={dismissStyle}
        aria-label="Dismiss announcement"
        data-testid="announcement-dismiss"
      >
        ✕
      </button>
    </div>
  );
}

const bannerStyle: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  padding: "10px 16px",
  borderTop: "1px solid #86efac",
  background: "var(--exms-success-soft)",
  color: "var(--exms-success-text)",
  flexShrink: 0,
  fontFamily: "system-ui, sans-serif",
};

const dismissStyle: CSSProperties = {
  background: "none",
  border: "none",
  fontSize: 13,
  cursor: "pointer",
  color: "var(--exms-success-text)",
  padding: "0 4px",
  marginLeft: 12,
  flexShrink: 0,
};
